import type { HTMLAttributes, ReactNode } from "react";

import { Chart } from "./Chart";
import type { ChartDatum } from "./chartTypes";

// StatCard is a KPI tile: a label, one big value, and an optional signed delta /
// sparkline. Despite the name it is NOT a card box —
// per UI-LAW §1/§2 it never combines border + background + padding into a
// panel; grouping is whitespace + type weight/size only, radius 0, no
// shadow. The big value uses the display type ramp; the delta uses the semantic
// success/error ramp ONLY for a genuine up/down direction (never a decorative
// second hue) and always ships an arrow glyph + worded aria-label so direction
// is never conveyed by color alone. The optional sparkline reuses the Chart
// primitive's line `size="sparkline"` form; Chart lazy-loads @visx behind
// its own chunk, so importing it here adds no static bundle weight.
//
// Live metrics are caller-provided; this primitive never fetches or synthesizes
// an aggregate from a keyset page.

export type StatCardDeltaSentiment = "positive" | "negative" | "neutral";

export interface StatCardDelta {
  /** Signed change; the sign drives the arrow + the default color direction. */
  value: number;
  /** The compared period (e.g. "vs last week"); rendered de-emphasized. */
  period?: string;
  /** Formats the delta magnitude (absolute value); default is a locale number. */
  formatter?: (magnitude: number) => string;
  /**
   * Overrides the sign→color mapping for metrics where a fall is good (e.g. a
   * dropping error rate is `positive`). Defaults to the sign: >0 positive, <0
   * negative, 0 neutral. The arrow always follows the literal sign.
   */
  sentiment?: StatCardDeltaSentiment;
}

export interface StatCardProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  label: string;
  value: ReactNode;
  delta?: StatCardDelta;
  sparkline?: ChartDatum[];
  tone?: "default" | "muted";
}

type DeltaDirection = "up" | "down" | "flat";

function classNames(classes: string[], className: string | undefined): string {
  if (className) {
    classes.push(className);
  }

  return classes.join(" ");
}

function deltaDirection(value: number): DeltaDirection {
  if (value > 0) {
    return "up";
  }

  if (value < 0) {
    return "down";
  }

  return "flat";
}

function deltaSentiment(delta: StatCardDelta): StatCardDeltaSentiment {
  if (delta.sentiment) {
    return delta.sentiment;
  }

  if (delta.value > 0) {
    return "positive";
  }

  if (delta.value < 0) {
    return "negative";
  }

  return "neutral";
}

function directionGlyph(direction: DeltaDirection): string | null {
  if (direction === "up") {
    return "↑";
  }

  if (direction === "down") {
    return "↓";
  }

  return null;
}

function directionWord(direction: DeltaDirection): string {
  if (direction === "up") {
    return "up";
  }

  if (direction === "down") {
    return "down";
  }

  return "unchanged";
}

function defaultDeltaMagnitude(magnitude: number): string {
  if (!Number.isFinite(magnitude)) {
    return "--";
  }

  if (Number.isInteger(magnitude)) {
    return magnitude.toLocaleString();
  }

  return magnitude.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function DeltaBadge({ delta }: { delta: StatCardDelta }) {
  const direction = deltaDirection(delta.value);
  const sentiment = deltaSentiment(delta);
  const glyph = directionGlyph(direction);
  const format = delta.formatter ?? defaultDeltaMagnitude;
  const magnitude = format(Math.abs(delta.value));
  const accessibleLabel = delta.period
    ? `${directionWord(direction)} ${magnitude} ${delta.period}`
    : `${directionWord(direction)} ${magnitude}`;

  return (
    <span
      aria-label={accessibleLabel}
      className={`marker-stat-card__delta marker-stat-card__delta--${sentiment}`}
    >
      {glyph ? (
        <span aria-hidden="true" className="marker-stat-card__delta-arrow">
          {glyph}
        </span>
      ) : null}
      <span aria-hidden="true">{magnitude}</span>
      {delta.period ? (
        <span aria-hidden="true" className="marker-stat-card__delta-period">
          {delta.period}
        </span>
      ) : null}
    </span>
  );
}

export function StatCard({
  className,
  delta,
  label,
  sparkline,
  tone = "default",
  value,
  ...props
}: StatCardProps) {
  const hasSparkline = sparkline !== undefined && sparkline.length > 0;

  return (
    <div className={classNames(["marker-stat-card"], className)} {...props}>
      <span className="marker-stat-card__label">{label}</span>
      <span
        className={classNames(
          ["marker-stat-card__value"],
          tone === "muted" ? "marker-stat-card__value--muted" : undefined,
        )}
      >
        {value}
      </span>
      {delta ? <DeltaBadge delta={delta} /> : null}
      {hasSparkline ? (
        <Chart
          ariaLabel={`${label} trend`}
          className="marker-stat-card__sparkline"
          data={sparkline}
          size="sparkline"
          variant="line"
        />
      ) : null}
    </div>
  );
}
